import {isEmpty} from "lodash";
import React, {ComponentType, memo, useCallback} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import ReactMarkdown, {type Components} from "react-markdown";
import {isPreview} from "../../redux/player/selectors";
import canSkipLevel from "../../redux/player/selectors/canSkipLevel";
import {Challenge, ChallengeMode} from "../../schema";
import {useGuide} from "../../providers/guide";
import Link from "../../ui/link";

type props = {
  challenge: Challenge;
  level: number;
} & Omit<React.ComponentProps<"div">, "children">;

type Component = ComponentType<props> & {
  Title: ComponentType<props>;
};

const introMessageMarkdownComponents: Partial<Components> = {
  a({node: _node, children, ...anchorProps}) {
    return (
      <a {...anchorProps} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

const IntroMessage: Component = Object.assign(
  memo<props>(function ({challenge, level, ...props}: props) {
    const {t} = useTranslation();
    const canSkip = useSelector(canSkipLevel);
    const preview = useSelector(isPreview);

    let text: string;

    switch (challenge.mode) {
      case ChallengeMode.COMPETENCE_TEST:
      case ChallengeMode.RPG_MODE:
        text = t("player.introMessage");
        break;
      case ChallengeMode.CHOICE_BASED_STUDY:
        text = t("player.introMessageTest");
        break;
      case ChallengeMode.CUSTOM_PARAMETERS_TEST:
      case ChallengeMode.CHOICES_WITH_CUSTOM_SCORES:
        text = t("player.introMessageQuiz");
        break;
      default:
        text = "[NotImplemented]";
        break;
    }

    if (challenge.startMessage && !isEmpty(challenge.startMessage)) {
      text = challenge.startMessage;
    }

    const {open: openGuide} = useGuide();

    const onHelpClick = useCallback(() => {
      openGuide("player.main");
    }, [openGuide]);

    return (
      <div {...props}>
        <div>
          <ReactMarkdown components={introMessageMarkdownComponents}>{text}</ReactMarkdown>
          <Link
            style={{
              font: "inherit",
              display: "inherit",
            }}
            onClick={onHelpClick}
          >
            {t("player.introGuideLink")}
          </Link>
          {t("player.introGuideInfo")}
        </div>
        {canSkip && !preview && "\n\n" + t("player.introSkipLevel")}
      </div>
    );
  }),
  {
    Title: memo<props>(function ({challenge, level, ...props}: props) {
      const {t} = useTranslation();
      let text: string;

      switch (challenge.mode) {
        case ChallengeMode.COMPETENCE_TEST:
        case ChallengeMode.RPG_MODE:
          text = t("player.introTitle");
          break;
        case ChallengeMode.CHOICE_BASED_STUDY:
        case ChallengeMode.CHOICES_WITH_CUSTOM_SCORES:
        case ChallengeMode.CUSTOM_PARAMETERS_TEST:
          text = t("player.introTitle");
          break;
        default:
          text = "[NotImplemented]";
          break;
      }

      return <span {...props}>{text}</span>;
    }),
  },
);

export default IntroMessage;
